import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import { Search as SearchIcon, Send as TransferIcon } from '@mui/icons-material';
import { useMasterData } from '../../../hooks/useMasterData';
import { SessionRecord } from '../../../services/master-data.service';
import { sessionTransferApi, SessionTransferStudent, SessionTransferResponse } from './sessionTransferApi';
import SessionTransferTable from './SessionTransferTable';

const normalizeClassName = (name?: string) =>
  String(name || '')
    .toUpperCase()
    .replace(/\./g, '')
    .replace(/\s+/g, ' ')
    .trim();

const getClassRank = (name?: string): number | null => {
  const n = normalizeClassName(name);
  const direct: Record<string, number> = {
    NURSERY: 0,
    'LKG': 1,
    'L K G': 1,
    'UKG': 2,
    'U K G': 2,
    FIRST: 3,
    SECOND: 4,
    THIRD: 5,
    FOURTH: 6,
    FIFTH: 7,
    SIXTH: 8,
    SEVENTH: 9,
    EIGHTH: 10,
    NINTH: 11,
    TENTH: 12,
  };

  if (direct[n] !== undefined) {
    return direct[n];
  }

  if (n.includes('ELEVENTH')) return 13;
  if (n.includes('TWELFTH') || n.includes('TWELTH')) return 14;

  return null;
};

const sortClassesForUi = (items: any[]) => {
  return [...items].sort((a, b) => {
    const aRank = getClassRank(a.name);
    const bRank = getClassRank(b.name);

    if (aRank != null && bRank != null && aRank !== bRank) {
      return aRank - bRank;
    }

    if (aRank != null && bRank == null) return -1;
    if (aRank == null && bRank != null) return 1;

    const aOrder = Number(a.sequenceOrder ?? Number.MAX_VALUE);
    const bOrder = Number(b.sequenceOrder ?? Number.MAX_VALUE);
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    return String(a.name || '').localeCompare(String(b.name || ''));
  });
};

const SessionTransferPage: React.FC = () => {
  const { masters, fetchMasters, fetchSections: fetchSectionsHook, fetchAcademicSessions } = useMasterData();
  
  // Filter state
  const [filters, setFilters] = useState({
    fromSessionId: '',
    classId: '',
    sectionId: '',
  });
  const [toSessionId, setToSessionId] = useState('');
  
  // Data state
  const [students, setStudents] = useState<SessionTransferStudent[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  
  // Selection & Transfer state
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [transferData, setTransferData] = useState<Record<string, { newClassId: string; newSectionId: string }>>({});
  const [classSectionsCache, setClassSectionsCache] = useState<Record<string, any[]>>({});
  
  // UI state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, []);

  useEffect(() => {
    if (filters.classId) {
      (async () => {
        const secs = await fetchSectionsHook(filters.classId);
        setSections(secs);
      })();
    } else {
      setSections([]);
    }
  }, [filters.classId]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleLoadStudents = async () => {
    if (!filters.fromSessionId || !filters.classId || !filters.sectionId) {
      setSnackbar({ open: true, message: 'Please select from session, class and section', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const data = await sessionTransferApi.preview(filters);
      setStudents(data);
      setSelectedStudents([]);

      if (!data || data.length === 0) {
        setSnackbar({
          open: true,
          message: 'No students found for the selected session, class and section.',
          severity: 'info',
        });
      }
      
      // Initialize transfer data with suggestion (next class)
      const initialTransferData: Record<string, { newClassId: string; newSectionId: string }> = {};
      const newCache = { ...classSectionsCache };
      
      for (const student of data) {
        const nextClassId = student.nextClassId || '';
        initialTransferData[student.id] = {
          newClassId: nextClassId,
          newSectionId: '',
        };
        
        if (nextClassId && !newCache[nextClassId]) {
          const secs = await fetchSectionsHook(nextClassId);
          newCache[nextClassId] = secs;
        }
      }
      
      setTransferData(initialTransferData);
      setClassSectionsCache(newCache);
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to load students', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTransferData = async (studentId: string, field: 'newClassId' | 'newSectionId', value: string) => {
    setTransferData((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value,
        ...(field === 'newClassId' ? { newSectionId: '' } : {}), // Reset section if class changes
      },
    }));

    if (field === 'newClassId' && value && !classSectionsCache[value]) {
      const secs = await fetchSectionsHook(value);
      setClassSectionsCache((prev) => ({ ...prev, [value]: secs }));
    }
  };

  const handleToggleSelect = (studentId: string) => {
    const currentIndex = selectedStudents.indexOf(studentId);
    const newSelected = [...selectedStudents];

    if (currentIndex === -1) {
      newSelected.push(studentId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedStudents(newSelected);
  };

  const handleToggleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = students.map((n) => n.id);
      setSelectedStudents(newSelecteds);
      return;
    }
    setSelectedStudents([]);
  };

  const handleExecuteTransfer = async () => {
    if (!toSessionId) {
      setSnackbar({ open: true, message: 'Please select target session', severity: 'error' });
      return;
    }

    if (toSessionId === filters.fromSessionId) {
      setSnackbar({ open: true, message: 'From session and target session cannot be same', severity: 'error' });
      return;
    }

    if (selectedStudents.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one student', severity: 'error' });
      return;
    }

    // Validate that all selected students have new class and section selected
    const invalidStudents = selectedStudents.filter(id => !transferData[id].newClassId || !transferData[id].newSectionId);
    if (invalidStudents.length > 0) {
      setSnackbar({ open: true, message: 'Please select new class and section for all selected students', severity: 'error' });
      return;
    }

    setConfirmOpen(true);
  };

  const processTransfer = async () => {
    setConfirmOpen(false);
    setExecuting(true);

    const payload = {
      fromSessionId: filters.fromSessionId,
      toSessionId,
      students: selectedStudents.map(id => ({
        studentId: id,
        newClassId: transferData[id].newClassId,
        newSectionId: transferData[id].newSectionId,
      })),
    };

    try {
      const result: SessionTransferResponse = await sessionTransferApi.execute(payload);
      const failedCount = result.failedStudents?.length || 0;
      setSnackbar({ 
        open: true, 
        message: failedCount > 0
          ? `Transfer complete. Transferred: ${result.studentsTransferred}, Skipped: ${result.studentsSkipped}. ${result.failedStudents?.[0]?.reason || ''}`
          : `Transfer complete. Transferred: ${result.studentsTransferred}, Skipped: ${result.studentsSkipped}`,
        severity: 'success' 
      });
      // Clear data after success or refresh?
      setStudents([]);
      setSelectedStudents([]);
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Transfer failed', severity: 'error' });
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 3 }}>
        <Typography variant="h4" gutterBottom>Session Transfer</Typography>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Filter Students</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="From Session"
                value={filters.fromSessionId}
                onChange={(e) => handleFilterChange('fromSessionId', e.target.value)}
              >
                {masters.academicSessions?.map((s: SessionRecord) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Class"
                value={filters.classId}
                onChange={(e) => handleFilterChange('classId', e.target.value)}
              >
                {sortClassesForUi(masters.classes || []).map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Section"
                disabled={!filters.classId}
                value={filters.sectionId}
                onChange={(e) => handleFilterChange('sectionId', e.target.value)}
              >
                {sections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={handleLoadStudents}
                disabled={loading}
              >
                Load Students
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {students.length > 0 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Students for Promotion/Migration</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  select
                  sx={{ minWidth: 200 }}
                  size="small"
                  label="Target Session"
                  value={toSessionId}
                  onChange={(e) => setToSessionId(e.target.value)}
                >
                  {masters.academicSessions?.map((s: SessionRecord) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={executing ? <CircularProgress size={20} /> : <TransferIcon />}
                  onClick={handleExecuteTransfer}
                  disabled={executing || selectedStudents.length === 0}
                >
                  Transfer Selected ({selectedStudents.length})
                </Button>
              </Box>
            </Box>

            <SessionTransferTable
              students={students}
              selectedStudents={selectedStudents}
              onToggleSelect={handleToggleSelect}
              onToggleSelectAll={handleToggleSelectAll}
              classes={masters.classes || []}
              sections={classSectionsCache}
              transferData={transferData}
              onUpdateTransferData={handleUpdateTransferData}
            />
          </Paper>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Session Transfer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to transfer {selectedStudents.length} selected students to the next session? 
            This will create new academic records and update their current class/section.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={processTransfer} color="primary" variant="contained">
            Confirm Transfer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SessionTransferPage;
